import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  DEFAULT_MEDICAL_IMAGE_HEIGHT,
  DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE,
  MedicalImageInterface,
} from '@/shared/config';
import { useMedia } from '@/shared/hooks';
import { InlineFormError, WidgetCard } from '@/shared/ui';

import { Group, medicalImageTitles, PathologyGroup } from '@/entities/assets';

import {
  FullHeadMprSliceGroupSection,
  FullHeadMprSlicesLoadingPlaceholder,
  FullHeadMprSliceZoomModal,
  TOOTH_BASIS_SERIES_ORDER,
  TOOTH_BASIS_SERIES_TO_GROUP,
  useFullHeadMprSlicesContext,
} from '@/widgets/FullHeadMPR';
import { MedicalImagesGroup } from '@/widgets/MedicalImageGroup';

import styles from './ToothMedicalImagesGroups.module.scss';

type ToothMedicalImagesGroupsProps = {
  toothID: string;
  groups: (Group | PathologyGroup)[];
  selectedMedicalImages?: MedicalImageInterface[];
  className?: string;
  toggleSelected?: (medicalImage: MedicalImageInterface) => void;
};

export const ToothMedicalImagesGroups: FC<ToothMedicalImagesGroupsProps> = (
  props,
) => {
  const { toothID, groups, selectedMedicalImages, className, toggleSelected } =
    props;

  const { formatMessage } = useIntl();

  const { isMobile } = useMedia();

  const {
    branch,
    canChangeOptions,
    fullHeadMprSliceGroups,
    isTileSelected,
    pendingTileIDs,
    recordsByTileID,
    resolveViewSelection,
    setSeriesOptions,
    setZoomedTileID,
    thicknessOptionsMm,
    toggleTile,
    toothInput,
    zoomedTile,
  } = useFullHeadMprSlicesContext();

  const sliceHeight = isMobile
    ? DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE
    : DEFAULT_MEDICAL_IMAGE_HEIGHT;

  const zoomedGroup =
    fullHeadMprSliceGroups.find(
      (group) => group.seriesName === zoomedTile?.seriesName,
    ) ?? null;

  return (
    <>
      {branch === 'server' && (
        <WidgetCard>
          <MedicalImagesGroup
            className={className}
            toothID={toothID}
            selectedMedicalImages={selectedMedicalImages}
            toggleSelected={toggleSelected}
            groups={groups}
          />
        </WidgetCard>
      )}
      {branch === 'failed' && (
        <WidgetCard>
          <InlineFormError
            className={className}
            errorMessage={
              <FormattedMessage
                id="global.defaultError"
                defaultMessage="Something went wrong. Try again later or contact our support team."
              />
            }
          />
        </WidgetCard>
      )}
      {(branch === 'skeleton' || branch === 'tiles') && (
        <WidgetCard>
          <div className={cn(styles.container, className)}>
            {branch === 'skeleton'
              ? TOOTH_BASIS_SERIES_ORDER.map((seriesName) => (
                  <div key={seriesName} className={styles.skeletonSection}>
                    <h4 className={isMobile ? 'h5' : 'h4'}>
                      {formatMessage(
                        medicalImageTitles[
                          TOOTH_BASIS_SERIES_TO_GROUP[seriesName]
                        ],
                      )}
                    </h4>
                    <FullHeadMprSlicesLoadingPlaceholder height={sliceHeight} />
                  </div>
                ))
              : fullHeadMprSliceGroups.map((group) => (
                  <FullHeadMprSliceGroupSection
                    key={group.seriesName}
                    canChangeOptions={canChangeOptions}
                    group={group}
                    groups={fullHeadMprSliceGroups}
                    pendingTileIDs={pendingTileIDs}
                    recordsByTileID={recordsByTileID}
                    thicknessOptionsMm={thicknessOptionsMm}
                    isTileSelected={isTileSelected}
                    onOptionsChange={setSeriesOptions}
                    onToggleTile={toggleTile}
                    onZoomTile={setZoomedTileID}
                  />
                ))}
          </div>
        </WidgetCard>
      )}
      <FullHeadMprSliceZoomModal
        group={zoomedGroup}
        groups={fullHeadMprSliceGroups}
        input={toothInput}
        tile={zoomedTile}
        onClose={() => setZoomedTileID('')}
        resolveViewSelection={resolveViewSelection}
      />
    </>
  );
};
